import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import SEO from './components/SEO';
import ShowcaseSection from './components/ShowcaseSection';
import VideoSection from './components/VideoSection';
import CommissionsInline from './components/CommissionsInline';
import PortfolioGallery from './components/PortfolioGallery';
import MarqueeStrip from './components/MarqueeStrip';
import Footer from './components/Footer';

/* ── Scrolling left-border strip ── */
const LeftBorder = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      const t = 'KNOWNDISC · BAD ART GETS BETTER · ';
      ref.current.textContent = t.repeat(40);
    }
  }, []);
  return (
    <div className="left-border" aria-hidden="true">
      <div ref={ref} className="scrolling-text" />
    </div>
  );
};

/* ── SINGLE SCROLL HOME ── */
function Home() {
  return (
    <main id="main-container">
      <SEO
        title="KNOWNDISC — Digital Artist"
        description="Digital artist. Two years of daily drawing. Bold character-driven illustrations, commissions, and an evolving art practice."
      />

      {/* 1 · HERO ─────────────────────────── */}
      <section id="home">
        <Hero />
      </section>

      {/* 2 · SELECTED WORK ──────────────── */}
      <section id="portfolio" className="page-section page-cream">
        <header className="editorial-head">
          <p className="editorial-label">Selected Work</p>
          <h2 className="editorial-title">The Gallery</h2>
          <p className="editorial-count">17 pieces</p>
        </header>
        <PortfolioGallery />
      </section>

      {/* 3 · MARQUEE ─────────────────────── */}
      <MarqueeStrip dark speed={28} />

      {/* 4 · DAILY SERIES ───────────────── */}
      <section className="page-section page-cream">
        <header className="editorial-head">
          <p className="editorial-label">Daily Practice</p>
          <h2 className="editorial-title">18 Days&nbsp;<span className="editorial-title-accent">Straight</span></h2>
        </header>
        <ShowcaseSection />
      </section>

      {/* 5 · VIDEO BREAKDOWN ────────────── */}
      <section className="page-section page-dark">
        <header className="editorial-head editorial-head--light">
          <p className="editorial-label">Making Of</p>
          <h2 className="editorial-title editorial-title--light">The&nbsp;Breakdown</h2>
        </header>
        <VideoSection />
      </section>

      {/* 6 · COMMISSIONS ────────────────── */}
      <section id="commissions" className="page-section page-white">
        <CommissionsInline />
      </section>

      {/* 7 · ABOUT ──────────────────────── */}
      <section id="about" className="page-section page-cream">
        <header className="editorial-head">
          <p className="editorial-label">Who</p>
          <h2 className="editorial-title">About&nbsp;Me</h2>
        </header>
        <About />
      </section>
    </main>
  );
}

/* ── App shell ── */
function AppContent() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="app-shell">
      <LeftBorder />
      <Header />
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0, transition: { duration: 0.18 } }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Routes location={location}>
            <Route path="/"            element={<Home />} />
            <Route path="/portfolio"   element={<Navigate to="/#portfolio"   replace />} />
            <Route path="/commissions" element={<Navigate to="/#commissions" replace />} />
            <Route path="/about"       element={<Navigate to="/#about"       replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
      <Footer />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);
  return <Router><AppContent /></Router>;
}
